import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useInView, animate } from "framer-motion";
import { BrandMark } from "../components/BrandLogo";
import {
  MessageSquareText, Bot, Inbox as InboxIcon, Mic, ScanEye, Flame,
  Languages, Tag, BellRing, BarChart3, ShieldCheck, CheckCheck,
  ArrowRight, Check, Menu, X, Star, Zap, Play, ChevronDown,
} from "lucide-react";

const GREEN = "#25D366";

const reveal = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } },
};
const stagger = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.09 } },
};

/* ---------------- Nav ---------------- */
function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const links = [
    { label: "Features", href: "#features" },
    { label: "How it works", href: "#how" },
    { label: "Pricing", href: "#pricing" },
    { label: "FAQ", href: "#faq" },
  ];
  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transform-gpu transition-[background-color,padding,box-shadow] duration-300 ${
        scrolled ? "bg-[#0B141A] border-b border-white/10 shadow-lg shadow-black/30 py-3" : "bg-[#0B141A]/95 md:bg-transparent py-5"
      }`}
      style={{ WebkitBackfaceVisibility: "hidden", backfaceVisibility: "hidden", willChange: "transform" }}
      data-testid="landing-nav"
    >
      <div className="mx-auto max-w-7xl px-5 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5" data-testid="nav-logo">
          <BrandMark size={38} />
          <span className="font-heading text-lg font-extrabold tracking-tight text-white">
            MuviArts<span className="text-[#25D366]">.Online</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="text-sm font-medium text-white/70 hover:text-white transition-colors"
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <Link
            to="/login"
            className="text-sm font-semibold text-white/80 hover:text-white px-4 py-2 transition-colors"
            data-testid="nav-login-btn"
          >
            Login
          </Link>
          <Link
            to="/login"
            className="group inline-flex items-center gap-2 text-sm font-bold text-[#0B141A] bg-[#25D366] hover:bg-[#20BD5A] rounded-full px-5 py-2.5 transition-all shadow-[0_0_25px_rgba(37,211,102,0.35)]"
            data-testid="nav-cta-btn"
          >
            Get Started
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </Link>
        </div>

        <button
          className="md:hidden text-white p-1.5"
          onClick={() => setOpen((v) => !v)}
          data-testid="nav-mobile-toggle"
          aria-label="Menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden mx-4 mt-3 rounded-2xl bg-[#111B21] border border-white/10 p-4 space-y-1" data-testid="nav-mobile-menu">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={() => setOpen(false)}
              className="block px-3 py-2.5 rounded-lg text-sm font-medium text-white/80 hover:bg-white/5"
            >
              {l.label}
            </a>
          ))}
          <Link to="/login" className="block px-3 py-2.5 rounded-lg text-sm font-medium text-white/80 hover:bg-white/5">Login</Link>
          <Link to="/login" className="mt-1 block text-center text-sm font-bold text-[#0B141A] bg-[#25D366] rounded-full px-5 py-2.5">Get Started</Link>
        </div>
      )}
    </header>
  );
}

/* ---------------- Animated chat mockup ---------------- */
const CHAT_SCRIPT = [
  { from: "them", text: "Hi! Do you deliver the wedding photo package to Dubai? 💍", d: 600 },
  { from: "typing", d: 1100 },
  { from: "me", text: "Absolutely! Our Dubai wedding package includes 2 photographers, a same day highlight reel & 300 edited photos. Want the price list? 📸", d: 1300 },
  { from: "them", text: "Yes please 🙏 also sending my budget as a voice note", d: 900 },
  { from: "voice", d: 900 },
  { from: "meta", text: "🎙️ Voice transcribed · 🔥 Lead marked HOT · Admin notified", d: 1200 },
];

function ChatMock() {
  const [step, setStep] = useState(0);
  useEffect(() => {
    let t;
    if (step < CHAT_SCRIPT.length) {
      t = setTimeout(() => setStep((s) => s + 1), CHAT_SCRIPT[step].d);
    } else {
      t = setTimeout(() => setStep(0), 2600);
    }
    return () => clearTimeout(t);
  }, [step]);

  const shown = CHAT_SCRIPT.slice(0, step);
  return (
    <div className="relative w-[300px] sm:w-[330px] mx-auto" data-testid="hero-chat-mock">
      {/* phone frame */}
      <div className="rounded-[2.4rem] bg-[#0B141A] p-2.5 shadow-2xl ring-1 ring-white/10">
        <div className="rounded-[1.9rem] overflow-hidden bg-[#EFEAE2]">
          {/* header */}
          <div className="flex items-center gap-3 bg-[#075E54] px-4 py-3">
            <div className="h-9 w-9 rounded-full bg-[#25D366]/90 grid place-items-center text-white font-bold">M</div>
            <div className="leading-tight">
              <p className="text-white text-sm font-semibold">MuviArts.Online</p>
              <p className="text-[#B7E4C7] text-[10px]">AI Agent · online</p>
            </div>
            <Bot className="ml-auto h-4 w-4 text-white/80" />
          </div>
          {/* body */}
          <div className="wa-chat-bg px-3 py-4 h-[380px] flex flex-col gap-2 justify-end overflow-hidden">
            {shown.map((m, i) => {
              if (m.from === "typing")
                return (
                  <motion.div key={i} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
                    className="self-start bg-white rounded-2xl rounded-tl-sm px-3.5 py-3 shadow-sm">
                    <span className="wa-typing"><i /><i /><i /></span>
                  </motion.div>
                );
              if (m.from === "voice")
                return (
                  <motion.div key={i} initial={{ opacity: 0, scale: 0.9, y: 8 }} animate={{ opacity: 1, scale: 1, y: 0 }}
                    transition={{ type: "spring", damping: 20 }}
                    className="self-end bg-[#D9FDD3] rounded-2xl rounded-tr-sm px-3 py-2.5 shadow-sm flex items-center gap-2 max-w-[80%]">
                    <Mic className="h-4 w-4 text-[#075E54]" />
                    <div className="flex items-end gap-0.5 h-4">
                      {[6, 12, 8, 16, 10, 14, 7].map((h, k) => (
                        <span key={k} className="w-0.5 rounded bg-[#075E54]/60" style={{ height: h }} />
                      ))}
                    </div>
                    <span className="text-[10px] text-[#54656F]">0:07</span>
                  </motion.div>
                );
              if (m.from === "meta")
                return (
                  <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                    className="self-center text-[10px] font-medium text-[#075E54] bg-[#E7FCE8] border border-[#25D366]/30 rounded-full px-3 py-1.5 shadow-sm">
                    {m.text}
                  </motion.div>
                );
              const me = m.from === "me";
              return (
                <motion.div key={i} initial={{ opacity: 0, scale: 0.92, y: 10 }} animate={{ opacity: 1, scale: 1, y: 0 }}
                  transition={{ type: "spring", damping: 20 }}
                  className={`${me ? "self-end bg-[#D9FDD3] rounded-tr-sm" : "self-start bg-white rounded-tl-sm"} max-w-[82%] rounded-2xl px-3.5 py-2 shadow-sm`}>
                  <p className="text-[13px] leading-snug text-[#111B21]">{m.text}</p>
                  <span className="flex items-center justify-end gap-0.5 text-[9px] text-[#54656F] mt-0.5">
                    12:0{i} {me && <CheckCheck className="h-3 w-3 text-[#34B7F1]" />}
                  </span>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
      {/* floating badges */}
      <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.6 }}
        className="absolute -left-6 top-24 hidden sm:flex items-center gap-2 bg-white rounded-xl shadow-lg px-3 py-2 border border-[#E9EDEF]">
        <Zap className="h-4 w-4 text-[#25D366]" />
        <span className="text-xs font-semibold text-[#111B21]">Replied in 1.8s</span>
      </motion.div>
      <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.9 }}
        className="absolute -right-4 bottom-16 hidden sm:flex items-center gap-2 bg-white rounded-xl shadow-lg px-3 py-2 border border-[#E9EDEF]">
        <Flame className="h-4 w-4 text-[#FF5722]" />
        <span className="text-xs font-semibold text-[#111B21]">Hot Lead 🔥</span>
      </motion.div>
    </div>
  );
}

/* ---------------- Hero ---------------- */
function Hero() {
  const badges = ["Official Meta Cloud API", "Anti Ban Guarantee", "No credit card"];
  return (
    <section className="relative overflow-hidden bg-[#0B141A] pt-32 pb-24">
      <div className="absolute -right-32 -top-32 h-[30rem] w-[30rem] rounded-full bg-[#128C7E]/30 blur-[120px]" />
      <div className="absolute -left-24 top-40 h-[26rem] w-[26rem] rounded-full bg-[#25D366]/20 blur-[120px]" />
      <div className="absolute inset-0 opacity-[0.04]"
        style={{ backgroundImage: "radial-gradient(#ffffff 1.2px, transparent 1.2px)", backgroundSize: "28px 28px" }} />

      <div className="relative mx-auto max-w-7xl px-5 grid lg:grid-cols-2 gap-14 items-center">
        <motion.div variants={stagger} initial="hidden" animate="show" className="text-center lg:text-left">
          <motion.div variants={reveal} className="inline-flex items-center gap-2 rounded-full bg-white/5 border border-white/10 px-3.5 py-1.5 mb-6">
            <span className="h-2 w-2 rounded-full bg-[#25D366] animate-glow" />
            <span className="text-xs font-medium text-[#B7E4C7]">AI WhatsApp Automation · Live</span>
          </motion.div>
          <motion.h1 variants={reveal} className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.05]">
            Turn WhatsApp chats into <span className="text-[#25D366]">24/7 automated revenue</span>
          </motion.h1>
          <motion.p variants={reveal} className="mt-6 text-base sm:text-lg text-white/70 max-w-xl mx-auto lg:mx-0 leading-relaxed">
            The AI CRM for the Official Meta WhatsApp Cloud API. Auto reply with your own trained AI, transcribe voice notes, verify payment proofs, and score Hot Leads 🔥 in real time.
          </motion.p>
          <motion.div variants={reveal} className="mt-8 flex flex-wrap items-center justify-center lg:justify-start gap-3">
            <Link to="/login"
              className="group inline-flex items-center gap-2 text-base font-bold text-[#0B141A] bg-[#25D366] hover:bg-[#20BD5A] rounded-full px-7 py-3.5 transition-all shadow-[0_0_35px_rgba(37,211,102,0.4)]"
              data-testid="hero-cta-btn">
              Start Automating Free
              <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            </Link>
            <a href="#how"
              className="inline-flex items-center gap-2 text-base font-semibold text-white border border-white/20 hover:border-white/40 rounded-full px-6 py-3.5 transition-colors"
              data-testid="hero-demo-btn">
              <Play className="h-4 w-4" /> See how it works
            </a>
          </motion.div>
          <motion.div variants={reveal} className="mt-8 flex flex-wrap justify-center lg:justify-start gap-x-6 gap-y-2">
            {badges.map((b) => (
              <span key={b} className="flex items-center gap-2 text-sm text-white/60">
                <ShieldCheck className="h-4 w-4 text-[#25D366]" /> {b}
              </span>
            ))}
          </motion.div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2 }}>
          <ChatMock />
        </motion.div>
      </div>
    </section>
  );
}

/* ---------------- Counter stat ---------------- */
function Stat({ value, suffix, label, prefix }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, value, { duration: 1.6, ease: "easeOut", onUpdate: (v) => setN(v) });
    return () => controls.stop();
  }, [inView, value]);
  const display = value % 1 !== 0 ? n.toFixed(1) : Math.round(n).toLocaleString();
  return (
    <div ref={ref} className="text-center" data-testid="landing-stat">
      <p className="font-heading text-3xl sm:text-4xl font-extrabold text-white">
        {prefix}{display}{suffix}
      </p>
      <p className="mt-1 text-sm text-white/50">{label}</p>
    </div>
  );
}
function Stats() {
  return (
    <section className="bg-[#075E54] py-14">
      <div className="mx-auto max-w-6xl px-5 grid grid-cols-2 md:grid-cols-4 gap-8">
        <Stat value={10} suffix="M+" label="Messages Automated" />
        <Stat value={99.8} suffix="%" label="AI Response Accuracy" />
        <Stat value={3.5} suffix="x" label="Conversion Lift" />
        <Stat value={2} prefix="<" suffix="s" label="Avg Reply Speed" />
      </div>
    </section>
  );
}

/* ---------------- Features ---------------- */
const FEATURES = [
  { icon: MessageSquareText, title: "Official Meta Cloud API", desc: "Connect your real WhatsApp Business number in 60 seconds. 99.9% uptime, zero ban risk.", span: "md:col-span-2", badge: "Meta Certified" },
  { icon: Bot, title: "RAG AI Auto Reply Agent", desc: "Trains on your PDFs, links & FAQs to answer complex queries 24/7.", badge: "24/7 Sales" },
  { icon: InboxIcon, title: "Smart Green Inbox", desc: "Familiar WhatsApp inbox with human take over, reactions & image lightbox." },
  { icon: Mic, title: "Voice Note Transcription", desc: "Turns incoming voice notes into clean text instantly." },
  { icon: ScanEye, title: "Image & Payment Proof AI", desc: "Vision AI reads receipts, transfer screenshots & product photos to verify orders.", span: "md:col-span-2", badge: "Vision AI" },
  { icon: Flame, title: "Lead Auto Ranking", desc: "Scores leads Cold ❄️ · Warm ☀️ · Hot 🔥 from buying intent.", badge: "Automated" },
  { icon: Languages, title: "AI Translator", desc: "Chat with buyers in 50+ languages, both directions." },
  { icon: Tag, title: "Offers & Quick Replies", desc: "Trigger discounts, packages & 1 click canned responses mid chat." },
  { icon: BellRing, title: "Admin WhatsApp Alerts", desc: "Get pinged the second a Hot Lead 🔥 needs you." },
  { icon: BarChart3, title: "Deep Analytics", desc: "Leads, response times & rank breakdowns by day, week or month.", span: "md:col-span-2" },
];
function Features() {
  return (
    <section id="features" className="bg-[#F0F2F5] py-24">
      <div className="mx-auto max-w-7xl px-5">
        <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true }} className="max-w-2xl mb-14 text-center md:text-left mx-auto md:mx-0">
          <motion.p variants={reveal} className="text-sm font-bold uppercase tracking-widest text-[#128C7E]">Everything included</motion.p>
          <motion.h2 variants={reveal} className="mt-3 font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-[#111B21]">
            One inbox. A full AI sales team.
          </motion.h2>
          <motion.p variants={reveal} className="mt-4 text-[#54656F] leading-relaxed">
            Every tool you need to capture, qualify and close customers on WhatsApp — powered by multimodal AI.
          </motion.p>
        </motion.div>

        <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }}
          className="grid md:grid-cols-4 gap-4" data-testid="features-grid">
          {FEATURES.map((f, i) => (
            <motion.div key={i} variants={reveal}
              whileHover={{ y: -5 }}
              className={`group rounded-2xl bg-white border border-[#E9EDEF] p-6 transition-shadow hover:shadow-[0_10px_40px_rgba(37,211,102,0.12)] text-center md:text-left ${f.span || ""}`}
              data-testid={`feature-card-${i}`}>
              <div className="flex items-center justify-center md:justify-start gap-3">
                <div className="grid place-items-center h-11 w-11 rounded-xl bg-[#E7FCE8] text-[#128C7E] group-hover:bg-[#25D366] group-hover:text-white transition-colors">
                  <f.icon className="h-5 w-5" />
                </div>
                {f.badge && <span className="text-[10px] font-bold uppercase tracking-wide text-[#128C7E] bg-[#E7FCE8] rounded-full px-2.5 py-1">{f.badge}</span>}
              </div>
              <h3 className="mt-4 font-heading text-lg font-bold text-[#111B21]">{f.title}</h3>
              <p className="mt-1.5 text-sm text-[#54656F] leading-relaxed">{f.desc}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

/* ---------------- How it works ---------------- */
const STEPS = [
  { n: "01", title: "Connect your WhatsApp", desc: "Link your official Meta WhatsApp Business number in under a minute — no code, no ban risk." },
  { n: "02", title: "Train your AI agent", desc: "Upload your catalog, FAQs, PDFs & offers. Your AI learns your business instantly." },
  { n: "03", title: "Watch it sell 24/7", desc: "AI replies, transcribes voice notes, verifies payments & tags Hot Leads while you sleep." },
];
function HowItWorks() {
  return (
    <section id="how" className="bg-[#0B141A] py-24 relative overflow-hidden">
      <div className="absolute left-1/2 top-0 h-96 w-96 -translate-x-1/2 rounded-full bg-[#128C7E]/20 blur-[120px]" />
      <div className="relative mx-auto max-w-6xl px-5">
        <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true }} className="text-center max-w-2xl mx-auto mb-16">
          <motion.p variants={reveal} className="text-sm font-bold uppercase tracking-widest text-[#25D366]">Live in 3 steps</motion.p>
          <motion.h2 variants={reveal} className="mt-3 font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-white">
            From zero to automated in minutes
          </motion.h2>
        </motion.div>
        <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true }} className="grid md:grid-cols-3 gap-6">
          {STEPS.map((s, i) => (
            <motion.div key={i} variants={reveal} className="relative rounded-2xl bg-white/5 border border-white/10 p-8 backdrop-blur-sm text-center md:text-left">
              <span className="font-heading text-5xl font-extrabold text-[#25D366]/25">{s.n}</span>
              <h3 className="mt-4 font-heading text-xl font-bold text-white">{s.title}</h3>
              <p className="mt-2 text-sm text-white/60 leading-relaxed">{s.desc}</p>
              {i < STEPS.length - 1 && (
                <ArrowRight className="hidden md:block absolute -right-5 top-1/2 -translate-y-1/2 h-6 w-6 text-white/20" />
              )}
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

/* ---------------- Pricing ---------------- */
const PLANS = [
  { name: "Starter", price: "$29", tagline: "For small businesses", popular: false,
    features: ["1 WhatsApp number", "AI auto reply agent", "500 AI chats / mo", "Voice transcription", "Lead ranking"] },
  { name: "Pro", price: "$79", tagline: "For scaling brands", popular: true,
    features: ["3 WhatsApp numbers", "Unlimited AI chats", "Payment proof vision AI", "AI translator (50+ langs)", "Hot lead WhatsApp alerts", "Deep analytics"] },
  { name: "Enterprise", price: "Custom", tagline: "High volume teams", popular: false,
    features: ["Unlimited numbers & seats", "Dedicated infra & SLA", "Custom AI training", "Priority support", "White glove onboarding"] },
];
function Pricing() {
  return (
    <section id="pricing" className="bg-[#F0F2F5] py-24">
      <div className="mx-auto max-w-6xl px-5">
        <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true }} className="text-center max-w-2xl mx-auto mb-14">
          <motion.p variants={reveal} className="text-sm font-bold uppercase tracking-widest text-[#128C7E]">Pricing</motion.p>
          <motion.h2 variants={reveal} className="mt-3 font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-[#111B21]">
            Simple plans that scale with you
          </motion.h2>
        </motion.div>
        <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true }} className="grid md:grid-cols-3 gap-6 items-stretch">
          {PLANS.map((p, i) => (
            <motion.div key={i} variants={reveal}
              className={`relative flex flex-col rounded-2xl p-7 border transition-all ${
                p.popular ? "bg-[#0B141A] border-[#25D366] shadow-[0_20px_60px_rgba(37,211,102,0.2)] md:-translate-y-3" : "bg-white border-[#E9EDEF]"
              }`}
              data-testid={`pricing-card-${p.name.toLowerCase()}`}>
              {p.popular && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 flex items-center gap-1 text-[11px] font-bold text-[#0B141A] bg-[#25D366] rounded-full px-3 py-1">
                  <Star className="h-3 w-3 fill-[#0B141A]" /> Most Popular
                </span>
              )}
              <p className={`font-heading text-lg font-bold ${p.popular ? "text-white" : "text-[#111B21]"}`}>{p.name}</p>
              <p className={`text-sm ${p.popular ? "text-white/50" : "text-[#54656F]"}`}>{p.tagline}</p>
              <div className="mt-5 flex items-end gap-1">
                <span className={`font-heading text-4xl font-extrabold ${p.popular ? "text-white" : "text-[#111B21]"}`}>{p.price}</span>
                {p.price !== "Custom" && <span className={`mb-1 text-sm ${p.popular ? "text-white/40" : "text-[#8696A0]"}`}>/mo</span>}
              </div>
              <ul className="mt-6 space-y-3 flex-1">
                {p.features.map((f) => (
                  <li key={f} className={`flex items-start gap-2.5 text-sm ${p.popular ? "text-white/80" : "text-[#54656F]"}`}>
                    <Check className="h-4 w-4 mt-0.5 shrink-0 text-[#25D366]" /> {f}
                  </li>
                ))}
              </ul>
              <Link to="/login"
                className={`mt-7 inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-bold transition-all ${
                  p.popular ? "bg-[#25D366] text-[#0B141A] hover:bg-[#20BD5A]" : "bg-[#111B21] text-white hover:bg-[#075E54]"
                }`}
                data-testid={`pricing-cta-${p.name.toLowerCase()}`}>
                {p.price === "Custom" ? "Contact Sales" : "Get Started"} <ArrowRight className="h-4 w-4" />
              </Link>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

/* ---------------- Testimonials ---------------- */
const TESTI = [
  { name: "Rahul & Meera", role: "Studio owners", img: "https://images.unsplash.com/photo-1753351055056-d612233d8036?crop=entropy&cs=srgb&fm=jpg&q=85",
    quote: "Our WhatsApp used to be chaos. Now the AI answers instantly and we only jump in for hot leads. Bookings up 3x." },
  { name: "Sofia Alvarez", role: "Boutique founder", img: "https://images.unsplash.com/photo-1758598306980-c47a94c6708c?crop=entropy&cs=srgb&fm=jpg&q=85",
    quote: "The payment proof reader alone saves us hours. It verifies transfers before we even open the chat." },
  { name: "Daniel Osei", role: "Café & catering", img: "https://images.unsplash.com/photo-1753351051161-21de8728ec17?crop=entropy&cs=srgb&fm=jpg&q=85",
    quote: "Voice note transcription is magic. Customers ramble, the AI summarises, we reply in seconds." },
];
function Testimonials() {
  return (
    <section className="bg-white py-24">
      <div className="mx-auto max-w-6xl px-5">
        <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true }} className="max-w-2xl mb-14 text-center md:text-left mx-auto md:mx-0">
          <motion.p variants={reveal} className="text-sm font-bold uppercase tracking-widest text-[#128C7E]">Loved by sellers</motion.p>
          <motion.h2 variants={reveal} className="mt-3 font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-[#111B21]">
            Real businesses. Real conversations. Real sales.
          </motion.h2>
        </motion.div>
        <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true }} className="grid md:grid-cols-3 gap-6">
          {TESTI.map((t, i) => (
            <motion.div key={i} variants={reveal} className="rounded-2xl bg-[#F0F2F5] border border-[#E9EDEF] p-7 text-center md:text-left">
              <div className="flex justify-center md:justify-start gap-1 text-[#FFB300] mb-4">
                {[...Array(5)].map((_, k) => <Star key={k} className="h-4 w-4 fill-[#FFB300]" />)}
              </div>
              <p className="text-[15px] text-[#111B21] leading-relaxed">“{t.quote}”</p>
              <div className="mt-5 flex items-center justify-center md:justify-start gap-3">
                <img src={t.img} alt={t.name} className="h-11 w-11 rounded-full object-cover" />
                <div>
                  <p className="text-sm font-bold text-[#111B21]">{t.name}</p>
                  <p className="text-xs text-[#54656F]">{t.role}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

/* ---------------- FAQ ---------------- */
const FAQS = [
  { q: "Will my number get banned?",
    a: "No. MuviArts.Online runs on the Official Meta WhatsApp Cloud API, the same infrastructure large brands use. There are no unofficial hacks or risky automation, so your number stays safe and fully compliant." },
  { q: "Do I need Meta approval?",
    a: "You connect your own WhatsApp Business number through Meta's official onboarding. We guide you through every step, and most sellers are live the same day. No developer required." },
  { q: "How long does setup take?",
    a: "Most businesses are automating chats within an hour. Connect your number, upload your FAQs and offers, and your AI agent starts replying instantly." },
  { q: "Can the AI reply in my customers' language?",
    a: "Yes. The AI understands and replies in 50+ languages, and you can translate any chat both ways right inside the inbox." },
  { q: "Do I need to know how to code?",
    a: "Not at all. Everything is point and click, from training your AI to setting up offers, quick replies and alerts." },
  { q: "What happens when a hot lead comes in?",
    a: "The moment a chat shows strong buying intent it is tagged Hot, and you get a WhatsApp alert on your own phone so you never miss a sale." },
];
function Faq() {
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" className="bg-[#F0F2F5] py-24">
      <div className="mx-auto max-w-3xl px-5">
        <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true }} className="mb-12 text-center md:text-left">
          <motion.p variants={reveal} className="text-sm font-bold uppercase tracking-widest text-[#128C7E]">FAQ</motion.p>
          <motion.h2 variants={reveal} className="mt-3 font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-[#111B21]">
            Questions? Answered.
          </motion.h2>
        </motion.div>
        <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true }} className="space-y-3" data-testid="faq-list">
          {FAQS.map((f, i) => {
            const isOpen = open === i;
            return (
              <motion.div key={i} variants={reveal}
                className={`rounded-2xl border bg-white transition-colors ${isOpen ? "border-[#25D366]" : "border-[#E9EDEF]"}`}
                data-testid={`faq-item-${i}`}>
                <button
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  className="w-full flex items-center justify-between gap-4 text-left px-5 py-4"
                  data-testid={`faq-question-${i}`}>
                  <span className="font-heading text-base font-bold text-[#111B21]">{f.q}</span>
                  <ChevronDown className={`h-5 w-5 shrink-0 text-[#128C7E] transition-transform ${isOpen ? "rotate-180" : ""}`} />
                </button>
                <motion.div
                  initial={false}
                  animate={{ height: isOpen ? "auto" : 0, opacity: isOpen ? 1 : 0 }}
                  transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                  className="overflow-hidden">
                  <p className="px-5 pb-5 text-sm text-[#54656F] leading-relaxed">{f.a}</p>
                </motion.div>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}

/* ---------------- CTA + Footer ---------------- */
function CtaFooter() {
  return (
    <>
      <section className="bg-[#075E54] py-20">
        <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true }}
          className="mx-auto max-w-4xl px-5 text-center">
          <motion.h2 variants={reveal} className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white leading-tight">
            Ready to automate your WhatsApp sales channel?
          </motion.h2>
          <motion.p variants={reveal} className="mt-4 text-white/70 max-w-xl mx-auto">
            Join sellers closing more deals on autopilot. Set up in minutes — no credit card required.
          </motion.p>
          <motion.div variants={reveal} className="mt-8">
            <Link to="/login"
              className="group inline-flex items-center gap-2 text-base font-bold text-[#075E54] bg-white hover:bg-[#E7FCE8] rounded-full px-8 py-4 transition-all shadow-xl"
              data-testid="footer-cta-btn">
              Start Automating Free
              <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            </Link>
          </motion.div>
        </motion.div>
      </section>

      <footer className="bg-[#0B141A] text-white/60 py-14">
        <div className="mx-auto max-w-7xl px-5 grid md:grid-cols-4 gap-10 text-center md:text-left">
          <div className="md:col-span-2">
            <div className="flex items-center justify-center md:justify-start gap-2.5">
              <BrandMark size={36} />
              <span className="font-heading text-lg font-extrabold text-white">MuviArts<span className="text-[#25D366]">.Online</span></span>
            </div>
            <p className="mt-4 text-sm max-w-sm mx-auto md:mx-0 leading-relaxed">
              AI powered WhatsApp automation & sales CRM built on the official Meta Cloud API.
            </p>
          </div>
          <div>
            <p className="font-semibold text-white text-sm mb-3">Product</p>
            <ul className="space-y-2 text-sm">
              <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
              <li><a href="#pricing" className="hover:text-white transition-colors">Pricing</a></li>
              <li><a href="#how" className="hover:text-white transition-colors">How it works</a></li>
              <li><Link to="/login" className="hover:text-white transition-colors">Login</Link></li>
            </ul>
          </div>
          <div>
            <p className="font-semibold text-white text-sm mb-3">Company</p>
            <ul className="space-y-2 text-sm">
              <li><a href="#" className="hover:text-white transition-colors">Privacy Policy</a></li>
              <li><a href="#" className="hover:text-white transition-colors">Terms of Service</a></li>
              <li><a href="#" className="hover:text-white transition-colors">Contact</a></li>
            </ul>
          </div>
        </div>
        <div className="mx-auto max-w-7xl px-5 mt-10 pt-6 border-t border-white/10 text-xs">
          © {new Date().getFullYear()} MuviArts.Online · Built on Official Meta WhatsApp Cloud API
        </div>
      </footer>
    </>
  );
}

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#0B141A]" data-testid="landing-page">
      <Nav />
      <Hero />
      <Stats />
      <Features />
      <HowItWorks />
      <Pricing />
      <Testimonials />
      <Faq />
      <CtaFooter />
    </div>
  );
}
